import React from 'react';
import type { LevelResponse } from '../types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Card de nível de acesso (view "Cards"). Padrão-lista travado: card flat/borda
 * SEM checkbox (F34 — seleção em massa só na tabela) + menu ••• de ações +
 * switch inline de status (Switch shared sm — F47).
 * Níveis não têm foto — o "avatar" é um box slate (#64748b) com ícone de escudo.
 */
export interface LevelCardProps {
  /** Level data from API */
  level: LevelResponse;
  /** Callback when edit is clicked */
  onEdit?: (id: string) => void;
  /** Callback when delete is clicked */
  onDelete?: (id: string) => void;
  /** Callback when activate/deactivate is clicked */
  onToggleStatus?: (id: string, currentStatus: string) => void;
  /** Navega para a lista de usuários filtrada por este nível */
  onViewUsers?: (id: string) => void;
  /** Duplica o nível (recebe o objeto completo p/ montar a cópia) */
  onDuplicate?: (level: LevelResponse) => void;
}

export const LevelCard: React.FC<LevelCardProps> = ({
  level,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewUsers,
  onDuplicate,
}) => {
  const modulesCount = Object.keys(level.permissions || {}).length;
  const isActive = level.status === 'active';

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(level.id) });
  if (onViewUsers) actions.push({ label: 'Ver usuários', icon: 'users', onClick: () => onViewUsers(level.id) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(level) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(level.id, level.status),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(level.id), variant: 'danger', dividerBefore: true });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`level-card-${level.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Box slate com ícone de escudo (avatar sem foto = slate) */}
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#64748b' }}
        >
          <Icon name="shield" className="w-5 h-5 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {level.name}
          </h3>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`level-card-actions-${level.id}`} />
          </div>
        )}
      </div>

      {/* Descrição */}
      <p
        className="mt-3 text-xs line-clamp-2"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        {level.description || 'Sem descrição'}
      </p>

      {/* Footer: módulos + status */}
      <div
        className="mt-3 pt-3 flex items-center gap-2"
        style={{ borderTop: '1px solid var(--theme-card-border)' }}
      >
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          <Icon name="layers" className="w-3 h-3" />
          {modulesCount} {modulesCount === 1 ? 'módulo' : 'módulos'}
        </span>
        {/* Status — F47: switch inline quando togglável; F62: StatusPill shared */}
        {onToggleStatus ? (
          <div className="ml-auto inline-flex items-center gap-2" data-testid={`level-card-toggle-${level.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(level.id, level.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Inativo'}
            className="ml-auto"
            data-testid={`level-card-status-${level.id}`}
          />
        )}
      </div>
    </div>
  );
};

LevelCard.displayName = 'LevelCard';
